import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Spin, Button, Table, Space, Modal, message, Input, Select, Empty } from "antd";
import get from "lodash/get";
import assign from "lodash/assign";
import filter from "lodash/filter";
import includes from "lodash/includes";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import flatMap from "lodash/flatMap";
import find from "lodash/find";
import trim from "lodash/trim";
import toLower from "lodash/toLower";

import { SearchOutlined } from "@ant-design/icons";
import { SUPER_ADMIN_ROLE_ACCESS, ORGANISATION_ADMIN_ROLE_IDs, ORGANISATION_MANAGER_IDs, ORGANISATION_SUPERVISORS_IDs } from "../../constants/userRoles";
import requireAuth from "../../hocs/requireAuth";

import { userColumns } from "../../constants/tableColumns/users";
import { fetchUsers, deleteUser, updateUser, getUserByID } from "../../redux/actions/userActions";
import { generateUserPayload, getUserCompanyIds } from "../../utilities/user";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { DELETE_USER_SUCCESS, USER_STATUS_UPDATE_SUCCESS } from "../../constants/message";
import AddEditUser from "../../components/users/addEditUser";
import { USER_ROLES } from "../../constants/userRoles";

const AllRoles = flatMap(USER_ROLES, (userRoles) => get(userRoles, 'ids'));


const UserSettingScreen = () => {
	const [loading, setLoading] = useState(false);
	const [companies, setCompanies] = useState([]);
	const inputRef = useRef(null);
	const dispatch = useDispatch();
	const [userForm, setUserForm] = useState({
		visible: false,
		data: null,
		edit: false,
	});

	const toggleUserForm = (data = null, edit = false) => {
		assign(userForm, {
			visible: !get(userForm, "visible"),
			data,
			edit,
		});
		setUserForm({ ...userForm });
	};

	const [users, setUsers] = useState({ data: [], users: [], query: "", roleFilter: [] });


	const onFilter = (query = get(users, "query"), roleFilter = get(users, "roleFilter")) => {
		const normalizedQuery = toLower(trim(query));
		const selectedIds = isEmpty(roleFilter)
			? []
			: flatMap(roleFilter, (role) => get(find(USER_ROLES, ["value", role]), "ids", []));

		const data = filter(get(users, "users"), (user) => {
			const matchesQuery =
				!normalizedQuery ||
				includes(toLower(get(user, "name", "")), normalizedQuery) ||
				includes(toLower(get(user, "email", "")), normalizedQuery);
			const matchesRole = isEmpty(selectedIds) || includes(selectedIds, get(user, "role.id"));
			return matchesQuery && matchesRole;
		});

		setUsers({
			...users,
			data,
			query,
			roleFilter,
		});
	};

	const onSearchUsers = debounce((query) => {
		onFilter(query, get(users, "roleFilter"));
	}, 300);


	const getUsers = () => {
		setLoading(true);
		dispatch(fetchUsers())
			.then((results) => {
				setLoading(false);
				const users = filter(get(results, 'list'), (user) => {
					return includes(AllRoles, get(user, 'role.id'))
				})
				setUsers({
					data: users,
					users,
					query: "",
					roleFilter: []
				});
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const onDeleteUser = (id) => {
		setLoading(true);
		dispatch(deleteUser(id))
			.then(() => {
				getUsers();
				message.success(DELETE_USER_SUCCESS);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	// Same update-user API and payload as the edit form, with only `enabled` changed.
	const onToggleStatus = (record, enabled) => {
		const id = get(record, "id");
		setLoading(true);
		dispatch(getUserByID(id))
			.then((user) => {
				const payload = generateUserPayload(
					{
						name: get(user, "name"),
						email: get(user, "email"),
						roleId: get(user, "role.id"),
						enabled,
						locations: getUserCompanyIds(user, companies),
					},
					companies
				);
				return dispatch(updateUser(id, { ...payload, version: get(user, "version", get(record, "version")) }));
			})
			.then(() => {
				message.success(USER_STATUS_UPDATE_SUCCESS);
				getUsers();
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const getCompanies = () => {
		setLoading(true);
		dispatch(fetchCompanies())
			.then((result) => {
				setLoading(false);
				setCompanies(result);
			})
			.catch(() => {
				setLoading(false);
			});
	};


	useEffect(() => {
		getUsers();
		getCompanies()
	}, []);

	return (
		<div className="pl-2 py-2 h-full flex flex-col">
			<div className="flex flex-row justify-between pb-4">
				<div>
					<p className="text-xl font-medium leading-[30px] text-black-1">Users</p>
				</div>
				<div>
					<Space>
						<Button
							type="primary"
							onClick={() => {
								toggleUserForm();
							}}
						>
							Add User
						</Button>
					</Space>
				</div>
			</div>

			<div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full ">
				<div className="w-full px-8 py-6">
					<div className="justify-end flex items-center h-full space-x-5 ">
						<Select
							mode="multiple"
							style={{
								minWidth: 220,
							}}
							maxTagCount="responsive"
							value={get(users, "roleFilter")}
							onChange={(roleFilter) => {
								onFilter(get(users, "query"), roleFilter);
							}}
							size="middle"
							placeholder="Filter by Role"
							allowClear
							options={USER_ROLES.map((role) => ({
								label: get(role, "label"),
								value: get(role, "value"),
							}))}
						/>
						<Input
							style={{
								width: 200,
							}}
							ref={inputRef}
							onChange={(val) => {
								onSearchUsers(val.target.value);
							}}
							size="middle"
							placeholder="Search User"
							onClear={() => {
								onFilter("", get(users, "roleFilter"));
							}}
							allowClear
							prefix={<SearchOutlined className="mr-2 text-gray-400" />}
						/>
					</div>
				</div>
				<div className="px-8 py-4 overflow-y-auto">
					<Spin spinning={loading} tip="Loading users...">
						<Table
							columns={userColumns(toggleUserForm, onDeleteUser, onToggleStatus)}
							dataSource={get(users, "data")}
							rowKey={(record) => get(record, "id")}
							rowClassName={(_, index) => (index % 2 === 1 ? "bg-grey-2" : "")}
							locale={{
								emptyText: (
									<Empty
										className="py-10"
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description={
											isEmpty(get(users, "users"))
												? "No users added yet"
												: "No users match your search"
										}
									/>
								),
							}}
						/>
					</Spin>
				</div>
			</div>

			<Modal
				destroyOnHidden
				title={get(userForm, "edit") ? "Edit User" : "Add User"}
				footer={false}
				onCancel={() => {
					toggleUserForm();
				}}
				open={get(userForm, "visible")}
			>
				<AddEditUser
					companies={companies}
					refresh={getUsers}
					toggle={toggleUserForm}
					data={get(userForm, "data")}
					edit={get(userForm, "edit")}
				/>
			</Modal>
		</div>
	);
};

export default requireAuth(UserSettingScreen, [...SUPER_ADMIN_ROLE_ACCESS,
...ORGANISATION_ADMIN_ROLE_IDs,
...ORGANISATION_MANAGER_IDs,
...ORGANISATION_SUPERVISORS_IDs
]);
